/** @file IK chain rotations as solved, each unwrapped next to its previous value across a drag. */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import { angle } from '@pose2d/math/angle';
import { localValue } from '../../panels/viewport/scene/localValue';
import type { ToolContext } from '../../panels/viewport/viewportTypes';
import type { IkRef } from './ikRef';
import type { IkExtras } from './ikTargetGesture';

/**
 * Setup extras of an IK target drag (R-E1-12, R-E1-13): every chain bone's local rotation after the solve
 * (`pose.applied`), unwrapped next to the previous move's value (the press-time local rotation first), as
 * rotateGesture does, so a drag that sweeps a bone past a half turn commits the turn it swept.
 */
export function chainRotations(ctx: Pick<ToolContext, 'pose'>, ik: IkRef): IkExtras {
  const prev = ik.chain.map((i) => localValue(ctx.pose, i, 'rotation'));
  return (now) => new Map(ik.chain.map((i, k) => {
    const near = prev[k] ?? 0;
    const solved = now.pose.applied[i * POSE_LAYOUT.LOCAL_STRIDE + POSE_LAYOUT.ROTATION] ?? near;
    const rotation = near + angle.shortest(near, solved);
    prev[k] = rotation;
    return [now.maps.boneIds[i] ?? '', { rotation }];
  }));
}
